import React, { useEffect, useRef, useState } from "react"
import {
    AccordionWithChildren,
    FieldsetFormGroup,
    FloatingLabelInput,
    InputLabel,
    merchantSelectors,
    PageContainer,
    PageWrapper,
    sortDescending,
    StandardButton,
    StandardButtonWithSpinner,
    toDict,
    userSelectors,
    useSelector,
} from "swiipe.portal.shared"
import SpinnerContainer from "../../components/buttons/SpinnerContainer"
import DropDownList from "../../components/form/input/DropDownList"
import CodeEditor from "../../components/form/syntax/CodeEditor"
import { useRequiredUserRelations } from "../../services/userRelationService"
import { debugInstallationSelectors } from "../../store/reducers/debugInstallationSlice"
import { StoreState } from "../../store/StoreState"
import {
    cacheClearThunk,
    cleanupDebugInstallationRequestsThunk,
    enableInstallationDebugThunk,
    ensureDebugInstallationsThunk,
    getDebugInstallationRequestsThunk,
} from "../../store/thunks/debugInstallationThunk"
import useReduxDispatch from "../../store/useReduxDispatch"
import { getTimeUntilShortString } from "../../util/dateUtil"
import "./DebugInstallationPage.scss"

interface IDebugInstallationPageProps {}

const cachTypes = [
    "OnboardingPayments",
    "IntegratorConnection",
    "Webshop",
    "WebshopDetails",
    "SwMerchant",
    "SwMerchantDetails",
    "ApiKey",
    "HealthStatus",
    "Installation",
    "Order",
] as const

type ECacheType = (typeof cachTypes)[number]

const DebugInstallationPage = ({}: IDebugInstallationPageProps) => {
    const dispatch = useReduxDispatch()
    useRequiredUserRelations([{ requiredType: "Merchant", requiredRole: 90 }])
    const user = useSelector(userSelectors.userData)
    const [installationId, setInstallationId] = useState<string | undefined>()
    const [minutesToDebug, setMinutesToDebug] = useState<number>(60)
    const [sessionId, setSessionId] = useState<string>(
        localStorage.getItem("debug-installation-session-id") ?? user?.user.email.split("@")[0] ?? ""
    )
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const installations = useSelector((state: StoreState) =>
        debugInstallationSelectors.installations(state, merchantDetails?.swMerchant.swMerchantId ?? "")
    )

    const requests = useSelector((state: StoreState) => debugInstallationSelectors.requests(state, installationId ?? ""))

    const installation = installations?.find((i) => i.installationId === installationId)

    const [showCacheClear, setShowCacheClear] = useState<boolean>(false)
    const [cacheType, setCacheType] = useState<ECacheType>("OnboardingPayments")
    const [cacheKey, setCacheKey] = useState<string>("")
    const webshopCacheTypes: ECacheType[] = ["HealthStatus", "OnboardingPayments", "Order", "Webshop", "WebshopDetails"]
    const merchantCacheTypes: ECacheType[] = ["ApiKey", "SwMerchant", "SwMerchantDetails"]
    const installationCacheTypes: ECacheType[] = ["Installation"]

    useEffect(() => {
        if (merchantDetails?.swMerchant.swMerchantId) {
            dispatch(ensureDebugInstallationsThunk(merchantDetails.swMerchant.swMerchantId, false))
        }
    }, [merchantDetails])

    useEffect(() => {
        if (installation) {
            dispatch(getDebugInstallationRequestsThunk(installation.installationId))
        }
    }, [installation])

    return (
        <PageWrapper fullWidth>
            <PageContainer classNameBody="debug-installation-page" title="Debug installations">
                <SpinnerContainer
                    className="debug-installation-page__select-installation"
                    showSpinner={!installations}
                    minHeight={!installations ? 500 : 0}
                >
                    {installations && (
                        <>
                            <h4>Installation:</h4>
                            <DropDownList
                                placeholder={"Choose installation"}
                                options={installations.map((i) => ({
                                    text: `${i.installationId} ${i.callbackEndpoint ? "- " + i.callbackEndpoint : ""}`,
                                    value: i.installationId,
                                }))}
                                value={installationId}
                                onChange={(e) => {
                                    setInstallationId(e.currentTarget.value)
                                }}
                            />
                        </>
                    )}
                </SpinnerContainer>
                {installation && (
                    <div className="debug-installation-page__enable-debug mt-4">
                        <h4>Enable debug:</h4>
                        <FloatingLabelInput
                            containerClass="debug-installation-page__enable-debug-input"
                            type="number"
                            placeholder="Minutes to debug"
                            value={minutesToDebug}
                            onChange={(e) => {
                                const val = e.currentTarget.value
                                const valInt = Math.max(0, Math.min(60 * 24, parseFloat(val)))
                                setMinutesToDebug(valInt)
                            }}
                        />
                        <FloatingLabelInput
                            containerClass="debug-installation-page__enable-debug-input mt-3"
                            type="text"
                            placeholder="SessionId"
                            value={sessionId}
                            onChange={(e) => {
                                const val = e.currentTarget.value
                                setSessionId(val)
                                const current = localStorage.getItem("debug-installation-session-id")
                                if (val.trim() !== current) {
                                    localStorage.setItem("debug-installation-session-id", val.trim())
                                }
                            }}
                        />
                        <div className="debug-installation-page__enable-debug-button-section">
                            <StandardButtonWithSpinner
                                title="Enable debugging"
                                containerClass="debug-installation-page__enable-debug-button"
                                onClick={async () => {
                                    try {
                                        await dispatch(
                                            enableInstallationDebugThunk(
                                                installation.swMerchantId ?? "",
                                                installation.installationId,
                                                minutesToDebug,
                                                sessionId
                                            )
                                        )
                                    } catch (ex) {
                                        // Ignore
                                    }
                                }}
                            />
                            <div className="debug-installation-page__enable-debug-info">
                                <div>
                                    Current debug until:{" "}
                                    {installation.debugEnabledUntil
                                        ? `${new Date(installation.debugEnabledUntil).toLocaleString()} - left: `
                                        : "Not enabled"}
                                    {installation.debugEnabledUntil && (
                                        <TimeUntil date={new Date(installation.debugEnabledUntil)} />
                                    )}
                                </div>
                                <div>Current sessionId: {installation.debugSessionId ?? ""}</div>
                            </div>
                        </div>
                    </div>
                )}

                {installation && (
                    <SpinnerContainer showSpinner={!requests} className="debug-installation-page__requests mt-4">
                        {requests && (
                            <div>
                                <h4>
                                    Requests:{" "}
                                    <StandardButtonWithSpinner
                                        invertedPurple
                                        containerClass="debug-installation-page__requests-refresh"
                                        title="Refresh"
                                        onClick={async () => {
                                            try {
                                                await dispatch(getDebugInstallationRequestsThunk(installation.installationId))
                                            } catch (ex) {
                                                // Ignore
                                            }
                                        }}
                                    />
                                    <StandardButtonWithSpinner
                                        invertedRed
                                        containerClass="debug-installation-page__requests-cleanup"
                                        title="Cleanup"
                                        onClick={async () => {
                                            try {
                                                await dispatch(cleanupDebugInstallationRequestsThunk(installation.installationId))
                                            } catch (ex) {
                                                // Ignore
                                            }
                                        }}
                                    />
                                </h4>
                                {requests.length === 0 && <div>No requests</div>}
                                {sortDescending(requests, (r) => r.requestStart).map((req) => {
                                    const requestFunctions: { function: string }[] = JSON.parse(req.requestFunctions) ?? []
                                    const errors: any[] = req.error ? JSON.parse(req.error) : []
                                    const anyErrors = Array.isArray(errors) && !!errors.find((e) => !!e)
                                    return (
                                        <AccordionWithChildren
                                            key={req.requestId}
                                            leftAlignHeader
                                            header={
                                                <span className="debug-installation-page__request-header">
                                                    <span className="debug-installation-page__request-header-time">
                                                        {new Date(req.requestStart).toLocaleString()}
                                                    </span>
                                                    <span className="debug-installation-page__request-header-functions">
                                                        {requestFunctions.map((f, index) => (
                                                            <b key={index} className="pl-2">
                                                                {f.function}
                                                            </b>
                                                        ))}
                                                    </span>
                                                    <span>
                                                        {new Date(req.requestEnd).getTime() -
                                                            new Date(req.requestStart).getTime()}
                                                        ms
                                                    </span>
                                                    {anyErrors && (
                                                        <span
                                                            className="debug-installation-page__request-header-error ml-3"
                                                            style={{ color: "red" }}
                                                        >
                                                            ERRORS
                                                        </span>
                                                    )}
                                                </span>
                                            }
                                        >
                                            <div>
                                                <h4>Request functions</h4>
                                                <div>
                                                    <CodeEditor
                                                        code={mapCodeToFunctionsObject(
                                                            requestFunctions,
                                                            req.requestFunctions ?? "[]"
                                                        )}
                                                        language={"json"}
                                                        jsonFormat
                                                    />
                                                </div>
                                                <h4>Response: {req.responseCode}</h4>
                                                <div>
                                                    <CodeEditor
                                                        code={mapCodeToFunctionsObject(requestFunctions, req.response ?? "[]")}
                                                        language={"json"}
                                                        jsonFormat
                                                    />
                                                </div>
                                                <h4>Error</h4>
                                                <div>
                                                    <CodeEditor
                                                        code={mapCodeToFunctionsObject(requestFunctions, req.error ?? "[]")}
                                                        language={"json"}
                                                        jsonFormat
                                                    />
                                                </div>
                                            </div>
                                        </AccordionWithChildren>
                                    )
                                })}
                            </div>
                        )}
                    </SpinnerContainer>
                )}
                <AccordionWithChildren
                    className="debug-installation-page_cache-clear-section"
                    header={<h4>Cache clearing</h4>}
                    leftAlignHeader
                    animate={false}
                    isExpanded={showCacheClear}
                    expandedDidChange={() => setShowCacheClear(!showCacheClear)}
                >
                    <FieldsetFormGroup field>
                        <b>Cache type:</b>
                        <DropDownList
                            style={{ maxWidth: 500 }}
                            placeholder={"Choose cache type"}
                            options={cachTypes.map((c) => ({
                                text: c,
                                value: c,
                            }))}
                            value={cacheType}
                            onChange={(e) => {
                                setCacheType(e.currentTarget.value as ECacheType)
                                setCacheKey(
                                    merchantCacheTypes.includes(e.currentTarget.value as ECacheType)
                                        ? merchantDetails?.swMerchant.swMerchantId ?? ""
                                        : ""
                                )
                            }}
                        />
                    </FieldsetFormGroup>
                    {!!merchantDetails?.webshops.length && webshopCacheTypes.includes(cacheType) && (
                        <FieldsetFormGroup field>
                            <b>Webshop IDs for global cache group:</b>
                            <DropDownList
                                style={{ maxWidth: 500 }}
                                placeholder={"Choose Webshop to select webshop ID as Global cache group"}
                                options={merchantDetails?.webshops.map((w) => ({
                                    text: w.hostName,
                                    value: w.webshopId,
                                }))}
                                value={cacheKey.split("_")[0]}
                                onChange={(e) => {
                                    setCacheKey(e.currentTarget.value)
                                }}
                            />
                        </FieldsetFormGroup>
                    )}
                    {!!installations?.length && installationCacheTypes.includes(cacheType) && (
                        <FieldsetFormGroup field>
                            <b>Installation IDs for global cache group:</b>
                            <DropDownList
                                style={{ maxWidth: 500 }}
                                placeholder={"Choose installation to select installation ID as Global cache group"}
                                options={installations.map((i) => ({
                                    text: `${i.installationId} ${i.callbackEndpoint ? "- " + i.callbackEndpoint : ""}`,
                                    value: i.installationId,
                                }))}
                                value={cacheKey}
                                onChange={(e) => {
                                    setCacheKey(e.currentTarget.value)
                                }}
                            />
                        </FieldsetFormGroup>
                    )}
                    {cacheType === "Order" && <i>For order cache, the cache key should be in format webshopId_platformOrderId</i>}
                    <FieldsetFormGroup field className="debug-installation-page__cache-key-container">
                        <InputLabel title="Global Cache Group:" className="debug-installation-page__cache-key-input-container">
                            <FloatingLabelInput
                                style={{ maxWidth: 500 }}
                                containerClass="debug-installation-page__enable-debug-input"
                                type="text"
                                placeholder="Global Cache Group"
                                value={cacheKey}
                                onChange={(e) => {
                                    setCacheKey(e.currentTarget.value)
                                }}
                                hidePlaceholder
                            />
                        </InputLabel>
                        {merchantDetails && cacheKey === merchantDetails.swMerchant.swMerchantId && (
                            <span className="debug-installation-page__cache-key-info">This is the merchant ID</span>
                        )}
                        {cacheType === "Order" &&
                            merchantDetails?.webshops.find(
                                (w) => cacheKey.startsWith(w.webshopId + "_") || w.webshopId === cacheKey
                            ) && (
                                <span className="debug-installation-page__cache-key-info">
                                    This is the webshop ID of{" "}
                                    {
                                        merchantDetails.webshops.find(
                                            (w) => cacheKey.startsWith(w.webshopId + "_") || w.webshopId === cacheKey
                                        )?.hostName
                                    }
                                    <br />
                                    {cacheKey.split("_").length > 1 && cacheKey.split("_")[1]?.trim() ? (
                                        <span>{`Platform order ID: ${cacheKey.substring(cacheKey.indexOf("_") + 1)}`}</span>
                                    ) : (
                                        <span style={{ color: "red" }}>Missing platform order ID</span>
                                    )}
                                </span>
                            )}
                        {cacheType !== "Order" && merchantDetails?.webshops.find((w) => w.webshopId === cacheKey) && (
                            <span className="debug-installation-page__cache-key-info">
                                This is the webshop ID of{" "}
                                {merchantDetails.webshops.find((w) => w.webshopId === cacheKey)?.hostName}
                            </span>
                        )}
                        {installations && installations.find((i) => i.installationId === cacheKey) && (
                            <span className="debug-installation-page__cache-key-info">
                                This is the installation ID of{" "}
                                {installations.find((i) => i.installationId === cacheKey)?.callbackEndpoint}
                            </span>
                        )}
                    </FieldsetFormGroup>

                    <div className="debug-installation-page_cache-clear-action-container">
                        <StandardButtonWithSpinner
                            width={300}
                            title="Perform cache clear"
                            disabled={!cacheKey.trim()}
                            onClick={async () => {
                                await dispatch(cacheClearThunk(cacheType, cacheKey.trim()))
                            }}
                        />
                    </div>
                </AccordionWithChildren>
            </PageContainer>
        </PageWrapper>
    )
}

function mapCodeToFunctionsObject(functions: { function: string }[], code: string) {
    if (!code) {
        return code
    }
    const json = JSON.parse(code)
    if (!Array.isArray(json)) {
        return code
    }
    return JSON.stringify(
        toDict(
            functions,
            (f) => f.function,
            (f, index) => (json.length > index ? json[index] : "")
        )
    )
}

const TimeUntil = ({ date }: { date: Date }) => {
    const tickRef = useRef<number>(0)
    const [tick, setTick] = useState(0)
    useEffect(() => {
        const interval = setInterval(() => {
            tickRef.current = tickRef.current + 1
            setTick(tickRef.current)
        }, 1000)
        return () => {
            clearInterval(interval)
        }
    }, [date])
    return new Date(date).getTime() < new Date().getTime() ? (
        <span>0</span>
    ) : (
        <span>{getTimeUntilShortString(new Date(date))}</span>
    )
}

export default DebugInstallationPage
